// frontend/src/hooks/usePowerData.ts
import { useQuery } from '@tanstack/react-query';
import { endOfDay } from 'date-fns';
import { getPowerData, getOutletRuntime } from '@/api/power';
import type { PowerResponse, OutletRuntimeResponse } from '@/api/power';
import { useTank } from '@/contexts/TankContext';
import { CACHE_TIMES } from '@/config/cacheTimes';

interface UsePowerDataOptions {
  startDate: Date;
  endDate: Date;
  readingType?: string;
  enabled?: boolean;
}

/** Format a Date as YYYY-MM-DD using local date parts (avoids timezone shift from toISOString). */
function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function usePowerData({ startDate, endDate, readingType, enabled = true }: UsePowerDataOptions) {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  // Date pickers return midnight (00:00:00) — push end date to 23:59:59.999
  // so the query includes the full end day.
  const endDateFull = endOfDay(endDate);

  return useQuery<PowerResponse>({
    queryKey: ['power-data', tankId, startDate.toISOString(), endDateFull.toISOString(), readingType],
    queryFn: () => getPowerData(
      tankId!,
      startDate.toISOString(),
      endDateFull.toISOString(),
      readingType,
    ),
    enabled: enabled && !!tankId,
    staleTime: CACHE_TIMES.DATA,
    placeholderData: (prev) => prev,
  });
}

export function useOutletRuntime(startDate: Date, endDate: Date) {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useQuery<OutletRuntimeResponse>({
    queryKey: ['outlet-runtime', tankId, formatLocalDate(startDate), formatLocalDate(endDate)],
    queryFn: () => getOutletRuntime(
      tankId!,
      formatLocalDate(startDate),
      formatLocalDate(endDate),
    ),
    enabled: !!tankId,
    staleTime: CACHE_TIMES.DATA,
    placeholderData: (prev) => prev,
  });
}
